import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { useGlobalContext } from "app/store/GlobalContext";
import logo from "assets/images/LUDVIG_CO_LOGO_RGB_WHITE.svg";
import { clearCompanyName, logoutUser } from "common/MetaActions";
import { getFavoriteCustomerId } from "common/customer/getFavoriteCustomer";
import config from "config/config";
import { useCustomerId } from "hooks/use-customer-id";
import { usePermission } from "hooks/use-permissions";
import { useSecondaryMenuItems } from "hooks/use-secondary-menu-items";
import { useRootStore } from "store";
import CustomerNavLinks from "./components/CustomerNavLinks";
import SecondaryMenu from "./components/SecondaryMenu";
import UserProfile from "./components/UserProfile";
import MainMenuFullscreen from "./components/mainMenu/MainMenuFullscreen";
import ExternalUserCustomerMenu from "./components/mainMenu/dropdownMenu/ExternalUserCustomerMenu";

import "./Header.scss";

export default function Header() {
  const { handleNavigation } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useRootStore((state) => state);
  const { myCustomers } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customers } = myCustomers;
  const { setUnreadThreadsStatus } = useRootStore((state) => state.status);
  const { resetInbox } = useRootStore((state) => state.inbox);
  const { resetArchive } = useRootStore((state) => state.archive);
  const isInternalUser = usePermission("isInternal");

  const { isAuthenticated } = user;

  const [toggleFullScreenMenu, setToggleFullScreenMenu] = useState(false);

  // Use the shared hook for secondary menu items
  const secondaryMenuItems = useSecondaryMenuItems();

  // Get customerId from URL
  const customerId = useCustomerId();

  const lastValidCustomerIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (customerId && customerId !== "my" && customerId !== "all") {
      lastValidCustomerIdRef.current = customerId;
    }
  }, [customerId]);

  // Update unread threads count when navigating between tabs
  useEffect(() => {
    const secondaryMenuPaths = [
      "/dashboard",
      "/inbox",
      "/archive",
      "/delivery",
      "/inventory",
      "/documents"
    ];

    const isSecondaryMenuPath = secondaryMenuPaths.some((path) =>
      location.pathname.includes(path)
    );

    if (isSecondaryMenuPath && customerId) {
      setUnreadThreadsStatus(customerId);
    }
  }, [location.pathname, customerId, setUnreadThreadsStatus]);

  const handleHomeClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>,
    onCloseMenu?: () => void
  ) => {
    e.preventDefault();

    const clickEvent = () => {
      if (onCloseMenu) {
        onCloseMenu();
      }
    };

    if (isAuthenticated) {
      if (isInternalUser) {
        handleNavigation({
          url: "/customers/my",
          eventTitle: "customers",
          e,
          clickEvent
        });
      } else {
        const favoriteCustomerId = getFavoriteCustomerId(user.userInfo?.id);
        const resolvedCustomerId =
          lastValidCustomerIdRef.current ??
          favoriteCustomerId ??
          customers[0]?.customerId;

        const url = resolvedCustomerId
          ? `/customers/${resolvedCustomerId}/dashboard`
          : "/customers/my";

        const eventTitle = resolvedCustomerId ? "dashboard" : "customers";

        handleNavigation({ url, eventTitle, e, clickEvent });
      }
    } else {
      handleNavigation({ url: "/", eventTitle: "root", e: e, clickEvent });
    }
  };

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };

  // Helper function to extract current path without customerId
  const getCurrentRoutePathWithoutCustomerId = (pathname: string): string => {
    // Extract path after /customers/{id}/
    const match = pathname.match(/\/customers\/[^/]+\/(.+)/);
    return match ? match[1] : "dashboard";
  };

  const handleChangeCustomerNavigation = (newCustomerId: string) => {
    if (newCustomerId !== customerId) {
      handleNavigation({
        url: `/customers/${newCustomerId}/dashboard`,
        clickEvent: () => handleChangeCustomer(newCustomerId)
      });
    }
  };

  const handleChangeCustomer = async (newCustomerId: string) => {
    // Clear company name
    clearCompanyName();

    // Reset inbox and archive state
    resetInbox();
    resetArchive();

    // Determine current route path without customerId
    const currentPath = getCurrentRoutePathWithoutCustomerId(location.pathname);

    // Navigate to same view but with new customerId
    navigate(`/customers/${newCustomerId}/${currentPath}`);
  };

  const renderLogo = () => {
    let logoText = "MittKontor";
    if (config.stage !== "production") {
      logoText += ` (${config.stage?.toUpperCase()})`;
    }

    return (
      <div className="logoHeader">
        <img
          alt="Ludvig &amp; Co"
          src={logo}
          onClick={(e) => {
            handleHomeClick(e, () => setToggleFullScreenMenu(false));
          }}
          style={{ cursor: "pointer" }}
        />
        <div className="logoText">{logoText}</div>
      </div>
    );
  };

  const showSecondaryMenu = () => {
    // Don't show on these paths
    const excludedPaths = [
      "/addCustomer",
      "/wizard",
      "/admin",
      "/settings",
      "/customers",
      "/customers/all",
      "/customers/my",
      "/unauthorized",
      "/"
    ];

    return !excludedPaths.includes(location.pathname);
  };

  const isLoggedIn = user.isAuthenticated;
  const shouldShowSecondaryMenu = showSecondaryMenu();

  return (
    <header className="header no-print">
      <div className="headerPrimaryRow">
        <div className="headerContent">
          <div className="headerLeft">
            {renderLogo()}
            {isLoggedIn && (
              <>
                {isInternalUser ? (
                  <>
                    <div className="headerDivider">|</div>
                    <CustomerNavLinks
                      handleChangeCustomerNavigation={
                        handleChangeCustomerNavigation
                      }
                    />
                  </>
                ) : (
                  customers.length > 1 && (
                    <div className="externalUserCustomerSection">
                      <div className="headerDivider">|</div>
                      <ExternalUserCustomerMenu
                        handleChangeCustomer={handleChangeCustomerNavigation}
                      />
                    </div>
                  )
                )}
              </>
            )}
          </div>

          {isLoggedIn && (
            <>
              <div className="headerRight">
                <UserProfile onLogout={handleLogout} />
              </div>
              <MainMenuFullscreen
                secondaryMenuItems={
                  shouldShowSecondaryMenu ? secondaryMenuItems : []
                }
                toggleFullScreenMenu={toggleFullScreenMenu}
                setToggleFullScreenMenu={setToggleFullScreenMenu}
                onLogout={handleLogout}
                userInfo={user.userInfo}
                customers={customers}
                handleChangeCustomer={handleChangeCustomerNavigation}
              />
            </>
          )}
        </div>
      </div>

      {isLoggedIn && shouldShowSecondaryMenu && (
        <div className="headerSecondaryRow">
          <SecondaryMenu menuItems={secondaryMenuItems} />
        </div>
      )}
    </header>
  );
}
